/**
 * Profit Share & Owner Drawings Service — BE-12 (`OwnerEquityController`). Real backend.
 * Returns `{ success, data }` with camelCase data, the shape the pages (and Team / Our Company) use.
 * Partners and approve / reject / reverse are the TC admin's; a dividend's response lists one drawing per partner.
 */

import ApiService from '../api.service';
import { keysCamelToSnake, unwrapData, unwrapList, unwrapPage } from '../../utils/apiConvert';

const clean = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v !== '' && v !== undefined && v !== null));
const ok = (data) => ({ success: true, data });

export const profitShareService = {
  // ── Partners ──────────────────────────────────────────────────────────

  // Each partner carries capitalAccount, drawingsAccount and its ownership history (newest first).
  getPartners: async () => ok(unwrapList(await ApiService.get('/api/partners')).items),

  // data: { name, ownershipPercent, role, phone, email, effectiveDate }
  createPartner: async (data) => ok(unwrapData(await ApiService.post('/api/partners', keysCamelToSnake(data)))),

  // Partial: only the fields sent. active false deactivates (share to 0); making it active again needs ownershipPercent.
  updatePartner: async (id, data) => ok(unwrapData(await ApiService.put(`/api/partners/${id}`, keysCamelToSnake(data)))),

  // ── Drawings ──────────────────────────────────────────────────────────

  // params: { partnerId, status, from, to, page, size } → Spring page
  getDrawings: async (params) =>
    ok(unwrapPage(await ApiService.get('/api/owner-drawings', { params: clean(params) }))),

  // data: { type, partnerId (drawing only), amount, paymentAccountId, date, resolutionReference (dividend), remarks }
  createDrawing: async (data) => ok(unwrapData(await ApiService.post('/api/owner-drawings', keysCamelToSnake(clean(data))))),

  approveDrawing: async (id) => ok(unwrapData(await ApiService.post(`/api/owner-drawings/${id}/approve`))),

  rejectDrawing: async (id, reason) => ok(unwrapData(await ApiService.post(`/api/owner-drawings/${id}/reject`, { reason }))),

  reverseDrawing: async (id, reason) => ok(unwrapData(await ApiService.post(`/api/owner-drawings/${id}/reverse`, { reason }))),

  attachReceipt: async (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return ok(unwrapData(await ApiService.post(`/api/owner-drawings/${id}/receipt`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })));
  },

  // → { downloadUrl, filename, contentType }
  getReceiptUrl: async (id) => ok(unwrapData(await ApiService.get(`/api/owner-drawings/${id}/receipt`))),

  // ── Profit Share Report ───────────────────────────────────────────────

  // params: { from, to } as YYYY-MM-DD, both required
  getProfitShare: async (params) => ok(unwrapData(await ApiService.get('/api/reports/profit-share', { params }))),

  // ── Payment Accounts (for dropdowns) ──────────────────────────────────

  // A drawing is paid from the TC's active bank (operations) or cash accounts.
  getPaymentAccounts: async () => {
    const { items } = unwrapList(await ApiService.getTcBankAccounts());
    return ok(items
      .filter((a) => a.isActive !== false && ['OPERATIONS', 'CASH'].includes(a.accountType))
      .map((a) => ({ id: a.id, name: [a.bankName, a.accountName].filter(Boolean).join(' - '), type: a.accountType })));
  },
};

export default profitShareService;
